import { Select, SelectItem, type SelectProps } from "@hopper-ui/components";
import { reloadPage } from "@sg-protect/core";
import config, { type Region } from "@sg-protect/runtime-config";
import { MixpanelEventName } from "../tracking/mixpanelEventName.ts";
import { MixpanelProperty } from "../tracking/mixpanelProperty.ts";
import { useTrackEvent } from "../tracking/useTrackEvent.ts";
import { useCallback } from "react";
import { DatabaseIcon } from "@sg-protect/components";
import { useShellTranslation } from "../i18n/i18next.ts";
import { REGION_LABELS, REGION_LABEL_KEYS } from "./regionLabels.ts";

interface RegionPickerProps extends Omit<SelectProps<object>, "children" | "onChange"> {
    onRegionChange?: (region: Region) => Promise<void>;
}

export function RegionPicker({ onRegionChange, ...props }: RegionPickerProps) {
    const { t } = useShellTranslation();
    const trackEvent = useTrackEvent();

    const onChange = useCallback(
        (value: Region) => {
            void trackEvent(
                MixpanelEventName.MenuItemClicked,
                {
                    [MixpanelProperty.Location]: "Region Picker",
                    [MixpanelProperty.Values]: REGION_LABELS[value]
                },
                {
                    // keepAlive ensures the tracking request completes before the page hard-refreshes after a region change.
                    keepAlive: true
                }
            );

            config.switch(value);
            void Promise.resolve()
                .then(() => onRegionChange?.(value))
                .finally(() => reloadPage());
        },
        [onRegionChange, trackEvent]
    );

    return (
        <Select
            prefix={<DatabaseIcon />}
            defaultValue={config.region}
            onChange={value => onChange(value as Region)}
            UNSAFE_width="200px"
            aria-label={t("RegionPicker.label")}
            {...props}
        >
            {config.regions.map(region => (
                <SelectItem data-telemetry={REGION_LABELS[region]} key={region} id={region}>
                    {t(REGION_LABEL_KEYS[region])}
                </SelectItem>
            ))}
        </Select>
    );
}
